import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const BackButton = ({ label = "Volver", path }) => {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: null, left: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const hasMoved = useRef(false);
  const dragStartPosition = useRef({ x: 0, y: 0 });
  const defaultPositionCalculated = useRef(false);

  // Función para calcular posición por defecto
  const calculateDefaultPosition = useCallback(() => {
    if (defaultPositionCalculated.current) return;
    
    const tryCalculate = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        setPosition({
          top: windowHeight - rect.height - 24,
          left: windowWidth - rect.width - 24
        });
        defaultPositionCalculated.current = true;
      } else {
        // Reintentar en el siguiente frame si el botón aún no está disponible
        requestAnimationFrame(tryCalculate);
      }
    };
    
    requestAnimationFrame(tryCalculate);
  }, []);

  // Cargar posición guardada desde localStorage al montar
  useEffect(() => {
    const savedPosition = localStorage.getItem('backButtonPosition');
    if (savedPosition) {
      try {
        const { top, left } = JSON.parse(savedPosition);
        setPosition({ top, left });
      } catch (error) {
        console.error('Error al cargar posición del botón:', error);
        // Calcular posición por defecto si hay error
        calculateDefaultPosition();
      }
    } else {
      // Calcular posición por defecto si no hay posición guardada
      calculateDefaultPosition();
    }
  }, [calculateDefaultPosition]);

  // Guardar posición en localStorage cuando cambie
  useEffect(() => {
    if (position.top !== null && position.left !== null) {
      localStorage.setItem('backButtonPosition', JSON.stringify({
        top: position.top,
        left: position.left
      }));
    }
  }, [position]);

  // Manejar inicio del arrastre
  const handleMouseDown = useCallback((e) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      dragStartPosition.current = { x: e.clientX, y: e.clientY };
      setIsDragging(true);
      hasMoved.current = false; // Resetear flag de movimiento
    }
  }, []);

  // Manejar movimiento durante el arrastre
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Calcular nueva posición
    let newLeft = e.clientX - dragOffset.x;
    let newTop = e.clientY - dragOffset.y;

    // Detectar si hubo movimiento significativo (más de 5px desde el inicio del arrastre)
    const moved = Math.abs(e.clientX - dragStartPosition.current.x) > 5 || 
                  Math.abs(e.clientY - dragStartPosition.current.y) > 5;
    if (moved) {
      hasMoved.current = true;
    }

    // Mantener límites para que el botón no se salga de la ventana
    newLeft = Math.max(0, Math.min(newLeft, windowWidth - buttonRect.width));
    newTop = Math.max(0, Math.min(newTop, windowHeight - buttonRect.height));

    setPosition({ top: newTop, left: newLeft });
  }, [isDragging, dragOffset]);

  // Manejar fin del arrastre
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Agregar event listeners para el arrastre
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevenir selección de texto durante el arrastre
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleClick = (e) => {
    // Solo navegar si no se estaba arrastrando y no hubo movimiento significativo
    if (!isDragging && !hasMoved.current) {
      if (path) {
        navigate(path);
      } else {
        navigate(-1); // Go back to the previous page
      }
    }
    // Resetear flag después de verificar
    hasMoved.current = false;
  };

  // Calcular estilos de posición
  const positionStyles = position.top !== null && position.left !== null
    ? { top: `${position.top}px`, left: `${position.left}px` }
    : { bottom: 24, right: 24 };

  return (
    <Button
      ref={buttonRef}
      variant="outlined" // Estilo outlined es más sutil y minimalista
      startIcon={<ArrowBackIcon />}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      sx={{
        position: "fixed",
        ...positionStyles,
        borderRadius: 8, // Botón píldora
        textTransform: "none",
        fontWeight: 600,
        backgroundColor: 'background.paper', // Para que sea legible sobre cualquier fondo
        boxShadow: 2,
        border: 'none',
        color: 'text.primary',
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
        userSelect: 'none',
        "&:hover": {
          backgroundColor: 'action.hover',
          boxShadow: 4,
        },
        "&:active": {
          cursor: 'grabbing',
        },
        zIndex: 1000,
      }}
    >
      {label}
    </Button>
  );
};

export default BackButton;
